export type ConversationStatus = "ready" | "working" | "complete" | "error";

type ConversationStatusInput = {
  hasConversation?: boolean;
  isComplete?: boolean;
  isError?: boolean;
  isOnboarding?: boolean;
  isWorking?: boolean;
  /**
   * A turn has run in this tab, for this dialogue. Defaults to false: the flag
   * exists to keep a conversation loaded from history from reporting
   * "complete", so a caller that omits it must not get that bug back. The
   * worst case of failing closed is a missing "complete" disc.
   */
  turnSeenForDialogue?: boolean;
};

export const conversationStatusMeta: Record<
  ConversationStatus,
  { label: string; color: string }
> = {
  ready: { label: "Ready", color: "#7f9397" },
  working: { label: "Agent working…", color: "#2395b7" },
  complete: { label: "Response complete", color: "#47c972" },
  error: { label: "Needs attention", color: "#d95852" },
};

export const getConversationStatus = ({
  hasConversation = false,
  isComplete = false,
  isError = false,
  isOnboarding = false,
  isWorking = false,
  turnSeenForDialogue = false,
}: ConversationStatusInput): ConversationStatus => {
  if (isError) return "error";
  // Onboarding replays pre-rendered template messages with no inference behind
  // them, so a working/complete chip beside OnboardingProgress is misleading.
  // A real error still wins: onboarding is the worst moment to hide one.
  if (isOnboarding) return "ready";
  if (isWorking) return "working";
  // "complete" is an *unseen response* marker. Any conversation loaded from
  // history also ends in an Assistant message, so it only counts once a turn
  // has actually run in the dialogue on screen.
  if (hasConversation && isComplete && turnSeenForDialogue) return "complete";
  return "ready";
};
